import { Injectable } from '@angular/core';
import * as markerjs3 from '@markerjs/markerjs3';

/**
 * Serviço responsável por gerenciar o MarkerArea (inicialização, listeners, restauração)
 */
@Injectable()
export class MarkerAreaManagerService {
    /**
     * Inicializa o MarkerArea para uma imagem
     */
    async initializeMarkerArea(
        image: HTMLImageElement,
        container: HTMLElement,
        onMarkerCreate?: (markerEditor: any) => void,
        onRender?: (event: any) => void
    ): Promise<markerjs3.MarkerArea> {
        return new Promise((resolve, reject) => {
            try {
                const markerArea = document.createElement('mjs-marker-area') as markerjs3.MarkerArea;
                (markerArea as any).targetImage = image;

                const updateDimensions = () => {
                    const visualWidth = image.clientWidth || image.offsetWidth || image.width;
                    const visualHeight = image.clientHeight || image.offsetHeight || image.height;
                    const naturalWidth = image.naturalWidth || image.width;
                    const naturalHeight = image.naturalHeight || image.height;

                    if (
                        naturalWidth > 0 &&
                        naturalHeight > 0 &&
                        visualWidth > 0 &&
                        visualHeight > 0
                    ) {
                        (markerArea as any).targetWidth = visualWidth;
                        (markerArea as any).targetHeight = visualHeight;
                        (markerArea as any)._naturalWidth = naturalWidth;
                        (markerArea as any)._naturalHeight = naturalHeight;
                    }
                };

                const waitForImageRender = () => {
                    const visualWidth = image.clientWidth || image.offsetWidth || 0;
                    const visualHeight = image.clientHeight || image.offsetHeight || 0;

                    if (visualWidth > 0 && visualHeight > 0) {
                        updateDimensions();
                    } else {
                        requestAnimationFrame(waitForImageRender);
                    }
                };

                updateDimensions();

                if (
                    !(markerArea as any).targetWidth ||
                    !(markerArea as any).targetHeight ||
                    (markerArea as any).targetWidth <= 0 ||
                    (markerArea as any).targetHeight <= 0
                ) {
                    waitForImageRender();
                }

                // Remove MarkerAreas antigos
                const oldMarkerAreas = container.querySelectorAll('mjs-marker-area');
                oldMarkerAreas.forEach(oldArea => {
                    try {
                        oldArea.remove();
                    } catch (e) {
                        // Ignora erros
                    }
                });

                container.appendChild(markerArea);

                const waitForMarkerAreaReady = async (): Promise<void> => {
                    try {
                        await customElements.whenDefined('mjs-marker-area');
                        await new Promise(resolve => requestAnimationFrame(resolve));
                        await new Promise(resolve => requestAnimationFrame(resolve));

                        if (markerArea && markerArea.isConnected) {
                            resolve(markerArea);
                        } else {
                            setTimeout(() => {
                                if (markerArea && markerArea.isConnected) {
                                    resolve(markerArea);
                                } else {
                                    resolve(markerArea);
                                }
                            }, 500);
                        }
                    } catch (e) {
                        setTimeout(() => resolve(markerArea), 500);
                    }
                };

                waitForMarkerAreaReady();

                // Configura eventos
                if (onMarkerCreate) {
                    markerArea.addEventListener('markercreate', (event: any) => {
                        if (event.detail && event.detail.markerEditor) {
                            setTimeout(() => {
                                onMarkerCreate(event.detail.markerEditor);
                            }, 50);
                        }
                    });
                }

                if (onRender) {
                    (markerArea as any).addEventListener('render', onRender);
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Configura listeners de seleção de marcadores
     */
    setupSelectionListeners(
        markerArea: markerjs3.MarkerArea,
        onSelectionChange: () => void
    ): () => void {
        const checkInterval = setInterval(() => {
            if (markerArea) {
                onSelectionChange();
            } else {
                clearInterval(checkInterval);
            }
        }, 300);

        (markerArea as any).addEventListener('markerselect', () => {
            setTimeout(() => onSelectionChange(), 50);
        });

        (markerArea as any).addEventListener('markerdeselect', () => {
            setTimeout(() => onSelectionChange(), 50);
        });

        (markerArea as any).addEventListener('areaclick', () => {
            setTimeout(() => onSelectionChange(), 100);
        });

        (markerArea as any).addEventListener('markercreate', () => {
            setTimeout(() => onSelectionChange(), 100);
        });

        // Retorna função para limpar o intervalo
        return () => clearInterval(checkInterval);
    }

    /**
     * Restaura o estado dos marcadores
     */
    restoreState(markerArea: markerjs3.MarkerArea, state: any): void {
        if (!markerArea || !state) return;

        try {
            markerArea.restoreState(state);
        } catch (error) {
        }
    }

    /**
     * Remove o MarkerArea do DOM
     */
    removeMarkerArea(markerArea: markerjs3.MarkerArea): void {
        if (!markerArea) return;

        try {
            if (markerArea.parentElement) {
                markerArea.parentElement.removeChild(markerArea);
            }
        } catch (error) {
        }
    }
}

